import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import ImageUpload from "@/components/ImageUpload";
import { Save, Palette, Image as ImageIcon, Contact as ContactIcon, Share2, BarChart3, SlidersHorizontal, RefreshCw } from "lucide-react";

const SECTIONS = [
  {
    key: "geral", label: "Geral", icon: SlidersHorizontal,
    fields: [
      { name: "site_name", label: "Nome do site" },
      { name: "slogan", label: "Slogan" },
      { name: "logo_url", label: "URL do logotipo", type: "image" },
      { name: "favicon_url", label: "URL do favicon", type: "image" },
      { name: "footer_description", label: "Descrição no rodapé", textarea: true, rows: 3 },
      { name: "footer_copyright", label: "Texto de copyright" },
      { name: "author_area_url", label: "URL — Área do Autor (link externo)" },
      { name: "author_area_label", label: "Rótulo do menu Área do Autor" },
    ],
  },
  {
    key: "contato", label: "Contato", icon: ContactIcon,
    fields: [
      { name: "phone", label: "Telefone" },
      { name: "whatsapp", label: "WhatsApp (só números, ex: 5592999990000)" },
      { name: "email", label: "E-mail de contato" },
      { name: "address", label: "Endereço" },
    ],
  },
  {
    key: "redes", label: "Redes sociais", icon: Share2,
    fields: [
      { name: "instagram", label: "Instagram (URL)" },
      { name: "facebook", label: "Facebook (URL)" },
      { name: "youtube", label: "YouTube (URL)" },
      { name: "linkedin", label: "LinkedIn (URL)" },
      { name: "tiktok", label: "TikTok (URL)" },
    ],
  },
  {
    key: "analytics", label: "Analytics", icon: BarChart3,
    fields: [
      { name: "google_analytics", label: "Google Analytics ID" },
      { name: "google_tag_manager", label: "Google Tag Manager ID" },
      { name: "meta_pixel", label: "Meta Pixel ID" },
    ],
  },
  {
    key: "tema", label: "Tema (cores)", icon: Palette,
    fields: [
      { name: "theme_primary", label: "Dourado principal", type: "color", help: "Cor de destaque (botões, títulos secundários)" },
      { name: "theme_primary_dark", label: "Dourado escuro", type: "color", help: "Sobre fundo claro (links, ícones)" },
      { name: "theme_primary_light", label: "Dourado claro", type: "color" },
      { name: "theme_dark_bg", label: "Fundo escuro", type: "color", help: "Hero, footer, painel admin" },
      { name: "theme_dark_bg_soft", label: "Fundo escuro suave", type: "color", help: "Cards no fundo escuro" },
      { name: "theme_light_bg", label: "Fundo claro (ivory)", type: "color" },
      { name: "theme_light_bg_soft", label: "Fundo claro suave (cream)", type: "color" },
      { name: "theme_text_on_dark", label: "Texto sobre fundo escuro", type: "color" },
      { name: "theme_text_on_dark_muted", label: "Texto secundário sobre escuro", type: "color", help: "Deve ter contraste AA (≥4.5:1)" },
      { name: "theme_text_on_light", label: "Texto sobre fundo claro", type: "color" },
      { name: "theme_text_on_light_muted", label: "Texto secundário sobre claro", type: "color", help: "Deve ter contraste AA (≥4.5:1)" },
    ],
  },
  {
    key: "imagens", label: "Imagens das páginas", icon: ImageIcon,
    fields: [
      { name: "home_about_image_1", label: "Home — Institucional (imagem esquerda)", type: "image" },
      { name: "home_about_image_2", label: "Home — Institucional (imagem direita)", type: "image" },
      { name: "about_hero_image", label: "Quem Somos — Hero", type: "image" },
      { name: "services_hero_image", label: "Serviços — Hero", type: "image" },
      { name: "bookstore_hero_image", label: "Livraria — Hero", type: "image" },
      { name: "authors_hero_image", label: "Autores — Hero", type: "image" },
      { name: "blog_hero_image", label: "Blog — Hero", type: "image" },
      { name: "events_hero_image", label: "Eventos — Hero", type: "image" },
      { name: "gallery_hero_image", label: "Galeria — Hero", type: "image" },
      { name: "faq_hero_image", label: "FAQ — Hero", type: "image" },
      { name: "contact_hero_image", label: "Contato — Hero", type: "image" },
      { name: "admin_login_image", label: "Login Admin — Imagem lateral", type: "image" },
    ],
  },
];

const THEME_DEFAULTS = {
  theme_primary: "#C5A059",
  theme_primary_dark: "#9A7B3E",
  theme_primary_light: "#E8D09B",
  theme_dark_bg: "#0A0A0A",
  theme_dark_bg_soft: "#1A1A1A",
  theme_light_bg: "#FDFBF7",
  theme_light_bg_soft: "#F0EBE1",
  theme_text_on_dark: "#FDFBF7",
  theme_text_on_dark_muted: "#C7C1B4",
  theme_text_on_light: "#171717",
  theme_text_on_light_muted: "#525252",
};

export default function AdminSettings() {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("geral");
  const { refresh } = useSettings();

  useEffect(() => { api.get("/settings").then((r) => setData(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", data);
      toast.success("Configurações atualizadas");
      refresh();
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const resetTheme = () => {
    if (!window.confirm("Restaurar cores padrão do tema?")) return;
    setData({ ...data, ...THEME_DEFAULTS });
    toast.success("Cores resetadas — clique em Salvar para aplicar");
  };

  const current = SECTIONS.find((s) => s.key === activeSection);

  const renderField = (f) => {
    const value = data[f.name] ?? "";
    if (f.type === "color") {
      return (
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={value || "#000000"}
            onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
            data-testid={`setting-${f.name}-picker`}
            className="h-10 w-16 bg-transparent border border-white/10 cursor-pointer p-1"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
            data-testid={`setting-${f.name}`}
            placeholder="#RRGGBB"
            className="flex-1 bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[color:var(--marupiara-gold)] focus:outline-none font-mono"
          />
        </div>
      );
    }
    if (f.type === "image") {
      return (
        <ImageUpload
          value={value}
          onChange={(url) => setData({ ...data, [f.name]: url })}
          testId={`setting-${f.name}`}
        />
      );
    }
    if (f.textarea) {
      return (
        <textarea
          rows={f.rows || 3}
          value={value}
          onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
          data-testid={`setting-${f.name}`}
          className="w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[color:var(--marupiara-gold)] focus:outline-none"
        />
      );
    }
    return (
      <input
        value={value}
        onChange={(e) => setData({ ...data, [f.name]: e.target.value })}
        data-testid={`setting-${f.name}`}
        className="w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 focus:border-[color:var(--marupiara-gold)] focus:outline-none"
      />
    );
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-[color:var(--marupiara-gold)]">Configurações Gerais</h1>
        <p className="text-white/70 text-sm mt-1">Dados globais do site — reflete em todo o portal</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Section tabs */}
        <aside className="lg:col-span-3">
          <nav className="bg-[color:var(--marupiara-charcoal)] border border-white/10 p-2 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                data-testid={`settings-tab-${s.key}`}
                className={`flex items-center gap-2 px-3 py-2.5 text-sm text-left whitespace-nowrap transition-colors ${activeSection === s.key ? "bg-[color:var(--marupiara-gold)]/15 text-[color:var(--marupiara-gold)] border-l-2 border-[color:var(--marupiara-gold)]" : "text-white/75 hover:bg-white/5"}`}
              >
                <s.icon size={16} /> {s.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Section content */}
        <div className="lg:col-span-9">
          <div className="bg-[color:var(--marupiara-charcoal)] border border-white/10 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-[color:var(--marupiara-gold)]">{current.label}</h2>
              {current.key === "tema" && (
                <button onClick={resetTheme} data-testid="theme-reset" className="text-xs text-white/70 hover:text-[color:var(--marupiara-gold)] flex items-center gap-1">
                  <RefreshCw size={12} /> Restaurar padrão
                </button>
              )}
            </div>
            <div className={`grid ${current.key === "tema" ? "md:grid-cols-2" : "md:grid-cols-2"} gap-5`}>
              {current.fields.map((f) => (
                <div key={f.name} className={f.textarea || f.type === "image" ? "md:col-span-2" : ""}>
                  <label className="overline text-[10px] text-white/75">{f.label}</label>
                  <div className="mt-1">{renderField(f)}</div>
                  {f.help && <p className="text-[11px] text-white/60 mt-1">{f.help}</p>}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button onClick={save} disabled={saving} data-testid="settings-save" className="btn-gold">
              <Save size={14} /> {saving ? "Salvando…" : "Salvar configurações"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
